import {useIntl} from 'react-intl'
import {useStatesGlobal} from '../../../../_gori/helpers/components/StatesGlobalProvider'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {cloneDeep, isEmpty} from 'lodash'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import SettingsService from '../core/_requests'
import OrderService from '../../orders/core/_requests'
import {OPTION_PACKAGE_TYPE} from '../../orders/core/_const'
import {useIntegrationStores} from '../../../../_gori/hooks/useIntegrationStores'

const PAGE_SIZE = 100

export const useAutomationRules = () => {
  const intl = useIntl()
  const {statesGlobal} = useStatesGlobal()
  const carriers = statesGlobal.carriers
  const {data: integrationStores} = useIntegrationStores(true)
  const {newCancelToken, isCancel} = useCancelToken()

  const carrierOptions = useMemo(() => {
    if (!carriers) return []

    const carrierList: Array<{value: string; label: string}> = Object.keys(carriers).reduce(
      (acc: Array<{value: string; label: string}>, key) => {
        const carrier = key.split('_')[0]
        if (!acc.find((item) => item.value === carrier)) {
          acc.push({
            value: carrier,
            label: carrier.toUpperCase(),
          })
        }
        return acc
      },
      []
    )
    return carrierList
  }, [carriers])

  const serviceOptions = useMemo(() => {
    if (isEmpty(carriers)) return

    const _options = Object.values(cloneDeep(carriers))
      .filter((value: any) => value?.status)
      .map((value: any) => {
        value.label = value.visuals?.display_name
        value.value = `${value.carrier}_${value.service}`
        return value
      })

    return [
      {
        label: intl.formatMessage({id: 'ALL'}),
        options: _options,
      },
    ]
  }, [carriers, intl])

  const [shipFromAddresses, setShipFromAddresses] = useState<any[]>([])

  const fetchShipFromAddresses = useCallback(async () => {
    try {
      const res = await SettingsService.getAddress({
        params: {page: 1, page_size: PAGE_SIZE, type: 'from'},
        cancelToken: newCancelToken(),
      })
      setShipFromAddresses(res.shipping_addresses || [])
    } catch (error: any) {
      if (isCancel(error)) return
    }
  }, [isCancel, newCancelToken])

  useEffect(() => {
    fetchShipFromAddresses()
  }, [fetchShipFromAddresses])

  const shipFromOptions = useMemo(() => {
    return shipFromAddresses.map((addr: any) => {
      const name = addr.company || `${addr.first_name || ''} ${addr.last_name || ''}`.trim()
      const addressStr = [addr.street1, addr.city, addr.state, addr.zip].filter(Boolean).join(', ')
      return {
        label: name ? `${name} - ${addressStr}` : addressStr,
        value: addr.id,
      }
    })
  }, [shipFromAddresses])

  const reloadShipFromAddresses = fetchShipFromAddresses

  const [packages, setPackages] = useState<any[]>([])

  const fetchPackages = useCallback(async () => {
    try {
      const res = await SettingsService.getPackages({
        params: {page: 1, page_size: PAGE_SIZE},
        cancelToken: newCancelToken(),
      })
      setPackages(res.packages || [])
    } catch (error: any) {
      if (isCancel(error)) return
    }
  }, [isCancel, newCancelToken])

  useEffect(() => {
    fetchPackages()
  }, [fetchPackages])

  const packageOptions = useMemo(() => {
    return packages.map((pkg: any) => ({
      label: `${pkg.name} (${pkg.length} x ${pkg.width} x ${pkg.height} ${pkg.unit || ''})`.trim(),
      value: pkg.id,
    }))
  }, [packages])

  const reloadPackages = fetchPackages

  const [tags, setTags] = useState<any[]>([])

  const fetchTags = useCallback(async () => {
    try {
      const res = await OrderService.getTags({cancelToken: newCancelToken()})
      setTags(res.tags || [])
    } catch (error: any) {
      if (isCancel(error)) return
    }
  }, [isCancel, newCancelToken])

  useEffect(() => {
    fetchTags()
  }, [fetchTags])

  const tagOptions = useMemo(() => {
    return tags.map((tag: any) => ({
      label: tag.name,
      value: tag.id,
      color: tag.color,
    }))
  }, [tags])

  const reloadTags = fetchTags

  const [presets, setPresets] = useState<any[]>([])

  const fetchPresets = useCallback(async () => {
    try {
      const res = await SettingsService.getShippingPresets({
        params: {page: 1, page_size: PAGE_SIZE},
        cancelToken: newCancelToken(),
      })
      setPresets(res.shipping_presets || [])
    } catch (error: any) {
      if (isCancel(error)) return
    }
  }, [isCancel, newCancelToken])

  useEffect(() => {
    fetchPresets()
  }, [fetchPresets])

  const presetOptions = useMemo(() => {
    return presets.map((preset: any) => ({
      label: preset.name,
      value: preset.id,
    }))
  }, [presets])

  const reloadPresets = fetchPresets

  const optionsPackageTypes = useMemo(() => {
    const carriersPackage = statesGlobal?.carriersPackage
    if (isEmpty(carriersPackage)) return [OPTION_PACKAGE_TYPE.CARRIER_STANDARD_PACKAGE]
    return carriersPackage.map((parent: any) => ({
      label: parent.label,
      options: parent?.value.map((item: any) => ({
        label: item.label,
        value: {
          length: item.parcel.length,
          width: item.parcel.width,
          height: item.parcel.height,
          dimension_unit: item.parcel.dimension_unit,
          weight_limit: item.parcel.weight_limit,
          package_type: item.value,
          package_id: null,
          package_count: item?.package_count || 1,
        },
      })),
    }))
  }, [statesGlobal?.carriersPackage])

  const optionsCustomPackage = useMemo(() => {
    const pkgs = statesGlobal?.packages
    if (!pkgs) return []
    return {
      label: intl.formatMessage({id: OPTION_PACKAGE_TYPE.CUSTOM_PACKAGE.label}),
      options: pkgs.map((item: any) => ({
        label: item.name,
        value: {
          length: item.length,
          width: item.width,
          height: item.height,
          dimension_unit: item.dimension_unit,
          package_id: item.id,
          package_type: null,
          package_count: item?.package_count || 1,
        },
      })),
    }
  }, [intl, statesGlobal?.packages])

  return {
    carrierOptions,
    serviceOptions,
    shipFromAddresses,
    shipFromOptions,
    reloadShipFromAddresses,
    packages,
    packageOptions,
    reloadPackages,
    tags,
    tagOptions,
    reloadTags,
    presets,
    presetOptions,
    reloadPresets,
    optionsPackageTypes,
    optionsCustomPackage,
    integrationStores,
  }
}
